import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Phone, ArrowRight, ShieldCheck, RotateCcw } from "lucide-react";
import { authAPI } from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_venue-onboard/artifacts/der85h7h_WhatsApp%20Image%202026-04-24%20at%2011.33.28.jpeg";

export default function LoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("bmm_token");
    const user = JSON.parse(localStorage.getItem("bmm_user") || "{}");
    if (token && user.role === "user") {
      navigate("/onboarding");
    }
  }, [navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (phone.length < 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    setLoading(true);
    try {
      await authAPI.requestOTP(phone);
      setStep("otp");
      setCountdown(60);
      toast.success("OTP sent to your phone");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 4) return;
    setLoading(true);
    try {
      const res = await authAPI.verifyOTP(phone, otp);
      localStorage.setItem("bmm_token", res.data.token);
      localStorage.setItem("bmm_user", JSON.stringify(res.data.user));
      toast.success("Login successful!");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setLoading(true);
    try {
      await authAPI.requestOTP(phone);
      setCountdown(60);
      setOtp("");
      toast.success("OTP resent");
    } catch (err) {
      toast.error("Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otp.length === 4 && step === "otp") {
      handleVerifyOTP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <div className="min-h-screen flex">
      {/* Left panel - Hero image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-slate-800/80 z-10" />
        <img
          src="https://images.unsplash.com/photo-1768851142314-c4ebf49ad45b?w=1200&q=80"
          alt="Venue"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 flex flex-col justify-end p-12 pb-16">
          <img src={LOGO_URL} alt="BookMyMandap" className="h-28 object-contain object-left mb-6 rounded bg-white/95 px-4 py-2 w-fit" />
          <p className="text-base text-white/80 leading-relaxed max-w-md mb-8">
            Join India's growing network of premium event venues. List your space, reach more customers, and grow your business.
          </p>
          <div className="border-t border-white/20 pt-5">
            <a href="https://www.bookmymandap.in" target="_blank" rel="noopener noreferrer" className="text-white font-semibold text-base hover:text-white/90 transition-colors">
              www.bookmymandap.in
            </a>
            <p className="text-white/60 text-sm mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Find & Book The Perfect Mandap Hassle-Free!
            </p>
          </div>
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-10">
            <img src={LOGO_URL} alt="BookMyMandap" className="h-16 object-contain mb-3" />
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#991B1B]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Venue Onboarding Portal
            </h2>
          </div>

          <Card className="border-slate-200 shadow-xl shadow-slate-200/50">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {step === "phone" ? "Welcome" : "Verify OTP"}
              </CardTitle>
              <CardDescription className="text-sm text-slate-500">
                {step === "phone"
                  ? "Enter your mobile number to get started"
                  : `We've sent a 4-digit code to +91 ${phone}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === "phone" ? (
                <form onSubmit={handleRequestOTP} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">Mobile Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        data-testid="login-phone-input"
                        id="phone"
                        type="tel"
                        placeholder="Enter 10-digit mobile number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="pl-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#991B1B]/20 focus:border-[#991B1B] h-12"
                        maxLength={10}
                      />
                    </div>
                  </div>
                  <Button
                    data-testid="login-request-otp-btn"
                    type="submit"
                    disabled={loading || phone.length < 10}
                    className="w-full h-12 bg-[#991B1B] text-white hover:bg-[#7F1D1D] hover:-translate-y-0.5 transition-all duration-200 shadow-md shadow-red-900/20 font-medium"
                  >
                    {loading ? "Sending..." : "Get OTP"}
                    {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <InputOTP
                      data-testid="login-otp-input"
                      maxLength={4}
                      value={otp}
                      onChange={setOtp}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                        <InputOTPSlot index={1} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                        <InputOTPSlot index={2} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                        <InputOTPSlot index={3} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  {loading && (
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                      <ShieldCheck className="h-4 w-4 animate-pulse" />
                      Verifying...
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <button
                      data-testid="login-resend-otp-btn"
                      onClick={handleResend}
                      disabled={countdown > 0 || loading}
                      className="text-[#991B1B] hover:text-[#7F1D1D] font-medium disabled:text-slate-400 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Resend OTP
                    </button>
                    {countdown > 0 && (
                      <span className="text-slate-400">{countdown}s</span>
                    )}
                  </div>

                  <Button
                    data-testid="login-change-number-btn"
                    variant="ghost"
                    onClick={() => { setStep("phone"); setOtp(""); }}
                    className="w-full text-slate-500 hover:text-slate-700"
                  >
                    Change phone number
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-xs text-slate-400 mt-6">
            By continuing, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
}
